import { AdminPageContent, AdminPageHeader } from "@/components/admin";
import { listSkillsPaginated } from "@/api/skills";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useDebouncedEffect, useMountEffect } from "@react-hookz/web";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
    PaginationState,
    RowSelectionState,
    SortingState,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { AddSkillButton } from "./components/AddSkillButton";
import { CoreSlotsPill } from "./components/CoreSlotsPill";
import { DataTable } from "./components/data-table";
import { SkillsToolbar } from "./components/SkillsToolbar";
import { SkillsTableToolbarActions } from "./components/SkillsTableToolbarActions";
import { columns } from "./components/columns";

function buildSortParams(sorting: SortingState) {
    return {
        sortBy: sorting[0]?.id,
        order: (sorting[0]?.desc ? "desc" : "asc") as "asc" | "desc",
    };
}

const Skills = () => {
    const { setBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Skills");

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [organizationName, setOrganizationName] = useState<string | null>(
        null
    );
    const [sorting, setSorting] = useState<SortingState>([
        { id: "name", desc: false },
    ]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    });
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    useMountEffect(() => {
        setBreadcrumbs([{ label: "Admin", path: "home" }, { label: "Skills" }]);
    });

    useDebouncedEffect(() => setDebouncedSearch(search), [search], 250, 1000);

    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
    }, [debouncedSearch, organizationId, sorting]);

    // Clear selection when page changes (stale selections from other pages are confusing)
    useEffect(() => {
        setRowSelection({});
    }, [pagination.pageIndex]);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [
            "admin-skills",
            pagination,
            debouncedSearch,
            organizationId,
            sorting,
        ],
        queryFn: () =>
            listSkillsPaginated({
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
                ...buildSortParams(sorting),
                organizationId: organizationId ?? undefined,
                search: debouncedSearch || undefined,
            }),
        placeholderData: keepPreviousData,
    });

    const hasActiveFilters = !!debouncedSearch || organizationId !== null;

    const selectedSkillId = Object.keys(rowSelection)[0];
    const selectedSkill =
        data?.items.find((s) => String(s.id) === selectedSkillId) ?? null;

    const clearAllFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setOrganizationId(null);
        setOrganizationName(null);
        setSorting([{ id: "name", desc: false }]);
        setRowSelection({});
    };

    return (
        <AdminPageContent>
            <AdminPageHeader
                title="Skills"
                subtitle="Create and publish reusable AI workflows"
            >
                {organizationId !== null && (
                    <CoreSlotsPill
                        organizationId={organizationId}
                        organizationName={organizationName}
                    />
                )}
                <AddSkillButton />
                <SkillsTableToolbarActions selectedSkill={selectedSkill} />
            </AdminPageHeader>

            <div className="mt-6">
                <SkillsToolbar
                    search={search}
                    onSearchChange={setSearch}
                    organizationId={organizationId}
                    organizationName={organizationName}
                    onOrgIdChange={setOrganizationId}
                    onOrgNameChange={setOrganizationName}
                />
                <DataTable
                    data={data?.items ?? []}
                    columns={columns}
                    rowCount={data?.total}
                    isLoading={isLoading}
                    isError={isError}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearAllFilters}
                    onRetry={() => refetch()}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    getRowId={(row) => String(row.id)}
                />
            </div>
        </AdminPageContent>
    );
};

export default Skills;
